import React, { useEffect, useState } from 'react';
import api from '../services/api';

interface Lead {
  id: string;
  homeowner_name: string;
  homeowner_phone: string;
  homeowner_email?: string;
  address: string;
  lead_source: string;
  job_type: string;
  hiring_position: string | null;
  created_at: string;
  lead_status: string;
  assigned_estimator: { id: string; name: string; email: string } | null;
  lead_interest: string | null;
  lead_situation: string | null;
  lead_form_notes: string | null;
  notes?: string;
}

interface Note {
  id: string;
  content: string;
  created_at: string;
  users: { name: string } | null;
}

interface Estimator {
  id: string;
  name: string;
  email: string;
  role: string;
}

const JOB_TYPE_META: Record<string, { label: string; color: string }> = {
  roofing:   { label: '🏠 Roofing',    color: '#4A7EB5' },
  insurance: { label: '🛡️ Insurance',  color: '#06B6D4' },
  concrete:  { label: '🪨 Concrete',    color: '#6B7280' },
  asphalt:   { label: '🛣️ Asphalt',    color: '#92400E' },
  hiring:    { label: '👤 Hiring',     color: '#8B5CF6' },
};

const HIRING_POSITION_META: Record<string, { label: string; color: string }> = {
  salesman: { label: 'Salesman', color: '#8B5CF6' },
  paver:    { label: 'Paver',    color: '#374151' },
  roofer:   { label: 'Roofer',   color: '#4A7EB5' },
  foreman:  { label: 'Foreman',  color: '#F59E0B' },
  laborer:  { label: 'Laborer',  color: '#6B7280' },
  concrete: { label: 'Concrete', color: '#6B7280' },
};

const SOURCE_META: Record<string, { label: string; color: string }> = {
  facebook:  { label: 'Facebook',  color: '#3B82F6' },
  instagram: { label: 'Instagram', color: '#EC4899' },
  website:   { label: 'Website',   color: '#10B981' },
  google:    { label: 'Google',    color: '#F59E0B' },
  meta:      { label: 'Meta',      color: '#4A7EB5' },
};

const STATUS_OPTIONS = [
  { key: 'new',             label: 'New',             color: '#4A7EB5' },
  { key: 'contacted',       label: 'Contacted',       color: '#F59E0B' },
  { key: 'appointment_set', label: 'Appointment Set', color: '#10B981' },
];

const STATUS_META: Record<string, { label: string; color: string }> = {
  new: { label: 'New', color: '#4A7EB5' },
  contacted: { label: 'Contacted', color: '#F59E0B' },
  appointment_set: { label: 'Appointment Set', color: '#10B981' },
  converted: { label: 'Converted', color: '#8B5CF6' },
  sold: { label: 'Sold', color: '#059669' },
  lost: { label: 'Lost', color: '#EF4444' },
};

const timeAgo = (iso: string) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.max(1, Math.floor(diffMs / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

interface Props {
  leadId: string | null;
  onClose: () => void;
  onUpdated: (lead: Lead) => void;
  estimators: Estimator[];
}

export default function LeadDetailPanel({ leadId, onClose, onUpdated, estimators }: Props) {
  const [lead, setLead] = useState<Lead | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!leadId) { setLead(null); return; }
    setLoading(true);
    Promise.all([
      api.get(`/leads/${leadId}`),
      api.get(`/leads/${leadId}/notes`).catch(() => ({ data: [] })),
    ]).then(([leadRes, notesRes]) => {
      setLead(leadRes.data);
      setNotes(notesRes.data || []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [leadId]);

  const updateLead = async (updates: Partial<Lead>) => {
    if (!lead) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/leads/${lead.id}/online-status`, updates);
      const updated = { ...lead, ...data };
      setLead(updated);
      onUpdated(updated);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const addNote = async () => {
    if (!lead || !newNote.trim()) return;
    setAddingNote(true);
    try {
      const { data } = await api.post(`/leads/${lead.id}/notes`, { content: newNote.trim() });
      setNotes(prev => [data, ...prev]);
      setNewNote('');
    } catch (e) { console.error(e); }
    finally { setAddingNote(false); }
  };

  const markLost = async () => {
    if (!lead || !window.confirm(`Mark ${lead.homeowner_name} as Lost?`)) return;
    await updateLead({ lead_status: 'lost' });
  };

  if (!leadId) return null;

  const source = lead ? (SOURCE_META[lead.lead_source] || { label: lead.lead_source, color: '#4A7EB5' }) : null;
  const jobTypeMeta = lead ? (JOB_TYPE_META[lead.job_type] || { label: lead.job_type, color: '#4A7EB5' }) : null;
  // For hiring leads, show position if available
  const hiringPos = lead?.job_type === 'hiring' && lead?.hiring_position
    ? HIRING_POSITION_META[lead.hiring_position] || { label: lead.hiring_position, color: '#8B5CF6' }
    : null;
  const jobType = jobTypeMeta ? {
    ...jobTypeMeta,
    label: hiringPos ? `👤 Hiring - ${hiringPos.label}` : jobTypeMeta.label
  } : null;
  const status = lead ? (STATUS_META[lead.lead_status] || STATUS_META.new) : null;
  const ageMs = lead ? Date.now() - new Date(lead.created_at).getTime() : 0;
  const ageHours = ageMs / 3600000;
  const isStale = ageHours > 24 && lead?.lead_status === 'new';

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, backdropFilter: 'blur(2px)' }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 520, background: '#0F1923',
        borderLeft: '1px solid #1E3A5F',
        zIndex: 101, overflowY: 'auto',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.5)',
        display: 'flex', flexDirection: 'column',
      }}>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #1E3A5F', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: '#0F1923', position: 'sticky', top: 0, zIndex: 10 }}>
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              {source && (
                <span style={{ background: `${source.color}22`, color: source.color, border: `1px solid ${source.color}44`, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                  {source.label}
                </span>
              )}
              {jobType && (
                <span style={{ background: `${jobType.color}22`, color: jobType.color, border: `1px solid ${jobType.color}44`, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                  {jobType.label}
                </span>
              )}
              {status && (
                <span style={{ background: `${status.color}22`, color: status.color, border: `1px solid ${status.color}44`, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                  {status.label}
                </span>
              )}
              {lead?.lead_status === 'converted' && (
                <span style={{ background: '#8B5CF622', color: '#8B5CF6', border: '1px solid #8B5CF644', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                  ✅ Converted
                </span>
              )}
              {isStale && (
                <span style={{ background: '#EF444422', color: '#EF4444', border: '1px solid #EF444444', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                  ⚠️ Stale 24h+
                </span>
              )}
            </div>
            <div style={{ color: '#fff', fontSize: 22, fontWeight: 900 }}>{lead?.homeowner_name || '...'}</div>
            <div style={{ color: '#8FA8C0', fontSize: 13, marginTop: 2 }}>{lead ? timeAgo(lead.created_at) : ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#8FA8C0', cursor: 'pointer', fontSize: 22, padding: 4 }}>✕</button>
        </div>

        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8FA8C0' }}>Loading...</div>
        ) : lead ? (
          <div style={{ padding: 24, flex: 1 }}>

            {/* Contact Info */}
            <Section title="Contact">
              <InfoRow label="Address" value={lead.address} />
              {lead.homeowner_phone && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10, borderBottom: '1px solid #1E3A5F', marginBottom: 10 }}>
                  <span style={{ color: '#8FA8C0', fontSize: 13 }}>Phone</span>
                  <a href={`tel:${lead.homeowner_phone}`} style={{ color: '#4A7EB5', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
                    📞 {lead.homeowner_phone}
                  </a>
                </div>
              )}
              {lead.homeowner_email && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10, borderBottom: '1px solid #1E3A5F', marginBottom: 10 }}>
                  <span style={{ color: '#8FA8C0', fontSize: 13 }}>Email</span>
                  <a href={`mailto:${lead.homeowner_email}`} style={{ color: '#4A7EB5', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
                    ✉️ {lead.homeowner_email}
                  </a>
                </div>
              )}
              {lead.address && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#8FA8C0', fontSize: 13 }}>Maps</span>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(lead.address)}`} target="_blank" rel="noreferrer" style={{ color: '#4A7EB5', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
                    📍 Open in Maps
                  </a>
                </div>
              )}
            </Section>

            {/* Quick Actions */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 24 }}>
              {lead.homeowner_phone && (
                <a href={`tel:${lead.homeowner_phone}`} style={actionBtnStyle('#1A2B3C', '#4A7EB5')}>📞 Call</a>
              )}
              {lead.homeowner_email && (
                <a href={`mailto:${lead.homeowner_email}`} style={actionBtnStyle('#1A2B3C', '#4A7EB5')}>✉️ Email</a>
              )}
              {lead.address && (
                <a href={`https://maps.google.com/?q=${encodeURIComponent(lead.address)}`} target="_blank" rel="noreferrer" style={actionBtnStyle('#1A2B3C', '#4A7EB5')}>📍 Directions</a>
              )}
            </div>

            {/* Lead Details */}
            {(lead.lead_interest || lead.lead_situation || lead.lead_form_notes) && (
              <Section title="Lead Details">
                {lead.lead_interest && <InfoRow label="Interested In" value={lead.lead_interest.replace(/_/g, ' ')} />}
                {lead.lead_situation && <InfoRow label="Situation" value={lead.lead_situation.replace(/_/g, ' ')} />}
                {lead.lead_form_notes && <InfoRow label="Homeowner Notes" value={lead.lead_form_notes} />}
              </Section>
            )}

            {/* Status */}
            <Section title="Status">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {STATUS_OPTIONS.map(s => (
                  <button
                    key={s.key}
                    onClick={() => updateLead({ lead_status: s.key })}
                    disabled={saving}
                    style={{
                      padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      border: `1px solid ${lead.lead_status === s.key ? s.color : '#1E3A5F'}`,
                      background: lead.lead_status === s.key ? `${s.color}22` : '#1A2B3C',
                      color: lead.lead_status === s.key ? s.color : '#8FA8C0',
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </Section>

            {/* Estimator */}
            <Section title="Assigned Estimator">
              <select
                value={lead.assigned_estimator?.id || ''}
                onChange={e => updateLead({ assigned_estimator: estimators.find(est => est.id === e.target.value) as any })}
                disabled={saving}
                style={{ width: '100%', background: '#1A2B3C', border: '1px solid #1E3A5F', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 14 }}
              >
                <option value="">Unassigned</option>
                {estimators.map(est => (
                  <option key={est.id} value={est.id}>{est.name}</option>
                ))}
              </select>
            </Section>

            {/* Notes */}
            <Section title="Notes">
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addNote()}
                  placeholder="Add a note... (Enter to save)"
                  style={{ flex: 1, background: '#1A2B3C', border: '1px solid #1E3A5F', color: '#fff', padding: '10px 12px', borderRadius: 8, fontSize: 13 }}
                />
                <button
                  onClick={addNote}
                  disabled={addingNote || !newNote.trim()}
                  style={{ background: '#4A7EB5', border: 'none', color: '#fff', padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  {addingNote ? '...' : 'Add'}
                </button>
              </div>
              {notes.length === 0 && (
                <div style={{ color: '#4A6580', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>No notes yet</div>
              )}
              {notes.map(note => (
                <div key={note.id} style={{ background: '#1A2B3C', border: '1px solid #1E3A5F', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
                  <div style={{ color: '#fff', fontSize: 14, marginBottom: 6, lineHeight: 1.5 }}>{note.content}</div>
                  <div style={{ color: '#4A6580', fontSize: 11 }}>
                    {note.users?.name || 'Admin'} · {formatDate(note.created_at)}
                  </div>
                </div>
              ))}
            </Section>

            {/* Danger Zone */}
            <Section title="Actions">
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={markLost}
                  disabled={saving || lead.lead_status === 'lost'}
                  style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #EF444466', background: '#EF444411', color: '#EF4444', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                >
                  ✕ Mark as Lost
                </button>
              </div>
              <div style={{ marginTop: 10, color: '#4A6580', fontSize: 11 }}>
                Lead created {formatDate(lead.created_at)}
              </div>
            </Section>

          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8FA8C0' }}>Lead not found</div>
        )}
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ color: '#4A7EB5', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>{title}</div>
      <div style={{ background: '#1A2B3C', border: '1px solid #1E3A5F', borderRadius: 12, padding: '14px 16px' }}>
        {children}
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 10, borderBottom: '1px solid #1E3A5F', marginBottom: 10 }}>
      <span style={{ color: '#8FA8C0', fontSize: 13 }}>{label}</span>
      <span style={{ color: '#fff', fontSize: 14, fontWeight: 600, textAlign: 'right', maxWidth: 280 }}>{value}</span>
    </div>
  );
}

function actionBtnStyle(bg: string, color: string): React.CSSProperties {
  return {
    display: 'block', textAlign: 'center', padding: '10px 8px',
    borderRadius: 10, border: `1px solid #1E3A5F`,
    background: bg, color: color, fontWeight: 700, fontSize: 13,
    textDecoration: 'none', cursor: 'pointer',
  };
}
